import { useState, type ReactNode } from 'react';
import { Table2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { STORY_STATUS_LABEL, type StoryStatus } from '../../../lib/api/abnahme';

/**
 * Bausteine der Abnahme-Statistik (docs/abnahme-plan.md, Abschnitt 11).
 *
 * Farben kommen aus den CSS-Variablen unter `.viz-root` (index.css) — so gilt der
 * Dunkelmodus mit eigenen, validierten Stufen. Text trägt nie die Serienfarbe.
 */

/** Status in Reihenfolge des Durchlaufs; „abgelehnt“ ist kein Fortschritt, sondern Warnung. */
export const STATUS_FARBE: Record<StoryStatus, string> = {
  entwurf: 'var(--viz-seq-1)',
  geplant: 'var(--viz-seq-2)',
  in_umsetzung: 'var(--viz-seq-3)',
  bereit_zur_abnahme: 'var(--viz-seq-4)',
  // Das Ziel hebt sich als einzige grüne Fläche ab, statt der dunkelsten Blaustufe.
  abgenommen: 'var(--viz-erfolg)',
  abgelehnt: 'var(--viz-kritisch)',
};

/** Burn-up: Abgenommenes unten, damit der Fortschritt von unten wächst. */
export const STAPEL_REIHENFOLGE: StoryStatus[] = [
  'abgenommen',
  'bereit_zur_abnahme',
  'in_umsetzung',
  'geplant',
  'entwurf',
  'abgelehnt',
];

export const KAT_1 = 'var(--viz-kat-1)';
export const KAT_2 = 'var(--viz-kat-2)';
export const EINZELTON = 'var(--viz-seq-3)';
export const FLAECHE = 'var(--viz-flaeche)';

export const ACHSE = {
  tick: { fill: 'var(--viz-text)', fontSize: 12 },
  axisLine: { stroke: 'var(--viz-raster)' },
  tickLine: false as const,
};
export const RASTER = { stroke: 'var(--viz-raster)', strokeDasharray: '2 4', vertical: false };

export const tagKurz = (iso: string): string => {
  const wert = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(wert.getTime())
    ? iso
    : wert.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

export const tagLang = (iso: string): string => {
  const wert = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(wert.getTime()) ? iso : wert.toLocaleDateString('de-DE');
};

export const prozent = (wert: number | null | undefined): string =>
  wert === null || wert === undefined ? '—' : `${wert.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

export function Legende({ eintraege }: { eintraege: { label: string; farbe: string; form?: 'linie' | 'raute' | 'punkt' }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {eintraege.map((e) => (
        <li key={e.label} className="flex items-center gap-1.5">
          {e.form === 'linie' ? (
            <span className="inline-block h-0.5 w-3" style={{ background: e.farbe }} />
          ) : e.form === 'raute' ? (
            <span className="inline-block h-2 w-2 rotate-45" style={{ background: e.farbe }} />
          ) : e.form === 'punkt' ? (
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: e.farbe }} />
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: e.farbe }} />
          )}
          {e.label}
        </li>
      ))}
    </ul>
  );
}

export const statusLegende = (reihenfolge: StoryStatus[] = STAPEL_REIHENFOLGE) =>
  reihenfolge.map((status) => ({ label: STORY_STATUS_LABEL[status], farbe: STATUS_FARBE[status] }));

interface TooltipEintrag {
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
  dataKey?: string | number;
}

/** Tooltip für Recharts: Farbe am Punkt, Text in Textfarbe. */
export function VizTooltip({
  active,
  payload,
  label,
  labelFormat = (l) => String(l),
  wertFormat = (w) => String(w),
}: {
  active?: boolean;
  payload?: TooltipEintrag[];
  label?: string | number;
  labelFormat?: (label: string | number) => string;
  wertFormat?: (wert: number | string) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      {label !== undefined && <div className="mb-1 font-medium">{labelFormat(label)}</div>}
      <ul className="space-y-0.5">
        {payload.map((eintrag) => (
          <li key={String(eintrag.dataKey ?? eintrag.name)} className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: eintrag.color }} />
            <span className="text-muted-foreground">{eintrag.name}</span>
            <span className="ml-auto pl-3 font-medium tabular-nums">
              {Array.isArray(eintrag.value) ? eintrag.value.join('–') : wertFormat(eintrag.value ?? '')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface Tabelle {
  spalten: string[];
  zeilen: (string | number)[][];
}

/** Rahmen je Diagramm — mit Tabellenansicht, damit nichts nur über Farbe lesbar ist. */
export function ChartKarte({
  titel,
  beschreibung,
  legende,
  tabelle,
  children,
  className = '',
}: {
  titel: string;
  beschreibung?: ReactNode;
  legende?: ReactNode;
  tabelle?: Tabelle;
  children: ReactNode;
  className?: string;
}) {
  const [alsTabelle, setAlsTabelle] = useState(false);
  return (
    <Card className={`viz-root ${className}`}>
      <CardHeader className="space-y-1 pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{titel}</CardTitle>
          {tabelle && (
            <button
              type="button"
              onClick={() => setAlsTabelle((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted"
              aria-pressed={alsTabelle}
            >
              <Table2 className="h-3.5 w-3.5" />
              {alsTabelle ? 'Diagramm' : 'Tabelle'}
            </button>
          )}
        </div>
        {beschreibung && <CardDescription>{beschreibung}</CardDescription>}
        {legende && !alsTabelle && <div className="pt-1">{legende}</div>}
      </CardHeader>
      <CardContent>
        {alsTabelle && tabelle ? (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  {tabelle.spalten.map((spalte) => (
                    <th key={spalte} className="py-1 pr-3 font-medium">
                      {spalte}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tabelle.zeilen.map((zeile, i) => (
                  <tr key={i} className="border-b last:border-0">
                    {zeile.map((zelle, j) => (
                      <td key={j} className={`py-1 pr-3 ${typeof zelle === 'number' ? 'tabular-nums' : ''}`}>
                        {zelle}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

export function Kachel({
  titel,
  wert,
  zusatz,
  ton = 'normal',
}: {
  titel: string;
  wert: ReactNode;
  zusatz?: ReactNode;
  ton?: 'normal' | 'warnung';
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{titel}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${ton === 'warnung' ? 'text-red-700 dark:text-red-400' : ''}`}
      >
        {wert}
      </div>
      {zusatz && <div className="mt-0.5 text-xs text-muted-foreground">{zusatz}</div>}
    </div>
  );
}
